<template>
    <section
        v-if="items.length || pending"
        class="related-items"
        v-bind="$attrs"
        :style="{
            '--background-color': backgroundColor,
            '--text-color': textColor
        }"
    >
        <div class="related-items__wrapper">
            <div class="related-items__text">
                <h2 v-if="!!$slots.title || pending">
                    <Skeletor v-if="pending" />
                    <slot
                        v-else
                        name="title"
                    />
                </h2>

                <p
                    v-if="!!$slots.title || pending"
                    class="related-items__caption"
                >
                    <Skeletor v-if="pending" />

                    <slot
                        v-else
                        name="caption"
                    />
                </p>
            </div>

            <slot>
                <template v-if="pending">
                    <BaseCard
                        v-for="i in [1,2,3]"
                        :key="i"
                        class="related-items__card base-card--compact"
                    >
                        <template #header>
                            <Skeletor />
                        </template>
                        <template #description>
                            <Skeletor />
                        </template>
                        <template #link>
                            <Skeletor />
                        </template>
                        <template #image>
                            <Skeletor height="100%" />
                        </template>
                    </BaseCard>
                </template>
                <BaseCard
                    v-for="(item, index) in items"
                    v-else-if="items && items.length > 0"
                    :key="index"
                    class="related-items__card base-card--compact"
                    :link="item.uri"
                    :hoverable-image="false"
                    :images="item.image || item.contentHero?.[0]?.image || item.restaurantHero?.[0]?.images"
                    image-sizes="(min-width: 1440px) 355px, (min-width: 768px) 24.68vw, 64vw"
                >
                    <template #header>
                        <h3>
                            {{ item.title }}
                        </h3>
                    </template>
                    <template #description>
                        {{ item.description ||item.contentHero?.[0]?.description || item.restaurantHero?.[0]?.description }}
                    </template>

                    <template
                        v-if="item.uri"
                        #link
                    >
                        <BaseArrowLink
                            element="span"
                            label-position="left"
                        >
                            {{ $t('read-more') }}
                        </BaseArrowLink>
                    </template>
                </BaseCard>
            </slot>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Skeletor } from 'vue-skeletor';

import type { ColorPalette } from '~/composables/useColorPalette';
import { useColorPalette } from '~/composables/useColorPalette';

const { resolveColor, getTextColor } = useColorPalette();

interface Props {
    backgroundColor?: keyof ColorPalette;
    customBackgroundColor?: string;
    pending?: boolean;
    items?: any[];
}

defineOptions({
    inheritAttrs: false
});

const props = withDefaults(defineProps<Props>(), {
    backgroundColor: () => 'cream',
    customBackgroundColor: undefined,
    pending: () => false,
    items: () => []
});

const backgroundColor = computed(() => {
    if (props.customBackgroundColor) {
        return props.customBackgroundColor;
    }

    return resolveColor(props.backgroundColor);
});

const textColor = computed(() => getTextColor(props.backgroundColor));
</script>

<style lang="less">
.related-items {
    background-color: var(--background-color);
    color: var(--text-color);
    transition: background-color var(--transition-medium);
}

.related-items__wrapper {
    .base-grid(var(--grid-maxWidth-page), false);
    .base-grid-padding(top);
    .base-grid-padding(bottom);

    row-gap: var(--spacing-xl);

    .base-card__body,
    .base-arrow-link,
    .base-arrow-link:hover,
    .base-arrow-link:visited {
        color: var(--text-color);
    }

    @media @q-md-min {
        row-gap: 6rem;
        grid-template-columns: [left-side-gutter] var(--grid-gutter-width)
            [first] 1fr
            [left-gutter] var(--grid-gutter-width)
            [second] 1fr
            [right-gutter] var(--grid-gutter-width)
            [third] 1fr
            [right-side-gutter] var(--grid-gutter-width);
    }
}

.related-items__text {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    grid-column: first/third;
    grid-row: 1;

    @media @q-md-min {
        grid-column: first;
        grid-row: 1/3;
    }
}

.related-items__caption {
    .typography-small();

    display: none;

    @media @q-md-min {
        display: block;
    }
}

.related-items__card {
    &:nth-child(2),
    &:nth-child(4) {
        grid-column: left-gutter/right-side-gutter;

        @media @q-md-min {
            grid-column: second;
        }
    }

    &:nth-child(2) {
        grid-row: 2;

        @media @q-md-min {
            grid-row: 1;
        }
    }

    &:nth-child(3) {
        grid-column: first/third;
        grid-row: 3;

        @media @q-md-min {
            grid-column: third;
            grid-row: 1/3;
            align-self: center;
        }
    }

    &:nth-child(4) {
        grid-row: 4;

        @media @q-md-min {
            grid-row: 2;
        }
    }
}
</style>
